import { isValidElement, useEffect, useRef } from 'react';
import { C, card, sectionTitle, decouperMontant } from '../theme';

// ── Briques d'interface partagées par tous les profils ──────────

export function Page({ children }) {
  return <div style={{ padding: 'clamp(16px, 3vw, 28px)', paddingBottom: 48 }}>{children}</div>;
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
      <div style={{ minWidth: 0 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: C.text, letterSpacing: '-0.02em' }}>{title}</h1>
        {subtitle && <p style={{ margin: '4px 0 0', color: C.textMuted, fontSize: 14 }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

export function Card({ title, action, children, style, ...rest }) {
  return (
    <section style={{ ...card, ...style }} {...rest}>
      {(title || action) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
          {title && <h2 style={{ ...sectionTitle, margin: 0 }}>{title}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

/** Valeur affichée avec la devise en petit : « 6 599 800 FCFA » → 6 599 800 + FCFA discret.
 *  Accepte n'importe quelle valeur : seules les chaînes produites par fmtF() sont mises en forme. */
export function Valeur({ value }) {
  const m = decouperMontant(value);
  if (!m) return <>{value}</>;
  return (
    <span className="sp-montant">
      <span className="sp-montant-nb">{m.nombre}</span>
      <span className="sp-montant-dev">FCFA{m.suite}</span>
    </span>
  );
}

/** Tuile KPI : icône teintée + libellé + valeur (+ indication facultative).
 *  `icon` : composant (LuWallet) ou élément déjà rendu (<LuWallet />).
 *  Sur mobile, les tuiles passent sur 2 colonnes et l'icône se place au-dessus (index.css). */
export function StatCard({ icon: Icon, label, value, hint, color = C.info, tint }) {
  const icone = isValidElement(Icon) ? Icon : Icon ? <Icon aria-hidden /> : null;
  return (
    <div className="sp-stat" style={{ '--stat': color, '--stat-bg': tint || `color-mix(in srgb, ${color} 12%, white)` }}>
      {icone && <div className="sp-stat-icone">{icone}</div>}
      <div className="sp-stat-corps">
        <div className="sp-stat-label">{label}</div>
        <div className="sp-stat-valeur"><Valeur value={value} /></div>
        {hint && <div className="sp-stat-hint">{hint}</div>}
      </div>
    </div>
  );
}

export function Grid({ min = 220, gap = 16, children, style }) {
  return (
    <div className="sp-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(min(${min}px, 100%), 1fr))`, gap, ...style }}>
      {children}
    </div>
  );
}

const BADGE_TONES = {
  success: [C.successLight, '#047857'],
  danger:  [C.dangerLight,  '#B91C1C'],
  warn:    [C.warnLight,    '#B45309'],
  info:    [C.infoLight,    '#1D4ED8'],
  neutral: ['#F1F5F9',      '#475569'],
  purple:  [C.purpleLight,  '#6D28D9'],
};

export function Badge({ tone = 'neutral', children }) {
  const [bg, fg] = BADGE_TONES[tone] || BADGE_TONES.neutral;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', backgroundColor: bg, color: fg }}>
      {children}
    </span>
  );
}

/** Onglets : pleine largeur, défilement horizontal discret si l'écran est trop étroit,
 *  l'onglet actif reste toujours visible. Sur mobile, l'icône passe au-dessus du libellé. */
export function Tabs({ tabs, value, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    const actif = ref.current?.querySelector('[aria-selected="true"]');
    actif?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  }, [value]);
  return (
    <div ref={ref} role="tablist" className="sp-tabs" data-defile={tabs.length > 4 ? '' : undefined}>
      {tabs.map(t => {
        const actif = t.id === value;
        const Icon = t.icon;
        return (
          <button key={t.id} type="button" role="tab" aria-selected={actif} className="sp-tab" onClick={() => onChange(t.id)}>
            {Icon && <Icon aria-hidden />}<span>{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, text, action }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 16px', color: C.textMuted }}>
      {Icon && (
        <div style={{ width: 56, height: 56, borderRadius: 16, margin: '0 auto 14px', display: 'grid', placeItems: 'center', fontSize: 26, backgroundColor: C.separator, color: C.textMuted }}>
          <Icon aria-hidden />
        </div>
      )}
      <div style={{ fontWeight: 700, fontSize: 15, color: C.text }}>{title}</div>
      {text && <div style={{ fontSize: 13, marginTop: 4 }}>{text}</div>}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

export function Loader({ label = 'Chargement…', fullScreen = true }) {
  return (
    <div role="status" style={{ minHeight: fullScreen ? '100vh' : 240, display: 'grid', placeItems: 'center', backgroundColor: fullScreen ? C.bg : 'transparent' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 36, height: 36, margin: '0 auto 12px', borderRadius: '50%', border: '3px solid #CBD5E1', borderTopColor: 'var(--accent, #059669)', animation: 'sp-spin .8s linear infinite' }} />
        <p style={{ margin: 0, color: C.textMuted, fontSize: 14 }}>{label}</p>
      </div>
    </div>
  );
}

export function Button({ variant = 'primary', icon: Icon, children, style, ...rest }) {
  const variants = {
    primary:   { backgroundColor: 'var(--accent)', color: '#fff', border: '1px solid transparent' },
    secondary: { backgroundColor: C.surface, color: C.text, border: `1px solid ${C.border}` },
    danger:    { backgroundColor: C.dangerLight, color: '#B91C1C', border: '1px solid transparent' },
  };
  return (
    <button
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        minHeight: 40, padding: '0 16px', borderRadius: 10, fontWeight: 600, fontSize: 14,
        ...variants[variant], ...style,
      }}
      {...rest}
    >
      {Icon && <Icon aria-hidden />}{children}
    </button>
  );
}

/** Barre de progression horizontale avec libellé et pourcentage. */
export function ProgressRow({ label, value, pct, color }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 6, fontSize: 13 }}>
        <span style={{ color: C.text, fontWeight: 500 }}>{label}</span>
        <span style={{ color: C.textSub, fontVariantNumeric: 'tabular-nums' }}>
          {value && <span style={{ marginRight: 8 }}>{value}</span>}
          <strong style={{ color: C.text }}>{Math.round(pct)}%</strong>
        </span>
      </div>
      <div style={{ height: 8, backgroundColor: C.separator, borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(100, pct)}%`, height: '100%', backgroundColor: color, borderRadius: 4, transition: 'width .4s ease' }} />
      </div>
    </div>
  );
}
